"use client";

import { TrpcProvider } from "app/_trpc/trpc-provider";
import { signOut, useSession } from "next-auth/react";
import { SessionProvider } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import CacheProvider from "react-inlinesvg/provider";

import { WebPushProvider } from "@calcom/web/modules/notifications/components/WebPushContext";
import { NotificationSoundHandler } from "@calcom/web/components/notification-sound-handler";
import { MobileProvider } from "@calcom/web/modules/mobile/MobileProvider";

import useIsBookingPage from "@lib/hooks/useIsBookingPage";

import { GeoProvider } from "./GeoContext";

/**
 * Watches the session for a suspended-account error injected by the NextAuth
 * session callback.  When detected it signs the user out and redirects to the
 * suspended-account error page so the change takes effect immediately — even
 * if the user was already logged in when the suspension was applied.
 */
function SuspendedSessionGuard() {
  const { data: session } = useSession();
  const router = useRouter();

  useEffect(() => {
    if ((session as any)?.error === "UserAccountSuspended") {
      signOut({ redirect: false }).then(() => {
        router.replace("/auth/error?error=UserAccountSuspended");
      });
    }
  }, [(session as any)?.error]);

  return null;
}

type ProvidersProps = {
  isEmbed: boolean;
  children: React.ReactNode;
  nonce: string | undefined;
  country: string;
};
export function Providers({ isEmbed, children, country }: ProvidersProps) {
  const isBookingPage = useIsBookingPage();

  return (
    <GeoProvider country={country}>
      {/*
       * refetchOnWindowFocus disabled in dev:
       * In Replit dev the Next.js server sits behind an HTTPS proxy.  The built-in
       * NextAuth session handler uses a cookie-name that can mismatch the cookie the
       * browser actually holds (because of Turbopack env-injection timing), so the
       * default /api/auth/session can return {} on focus-refetches and briefly flip
       * useSession() to "unauthenticated", triggering a redirect loop.
       * We work around this with a custom /api/auth/session handler (pages/api/auth/session.ts)
       * that reads NEXTAUTH_URL at call-time and always uses the correct __Secure- cookie name.
       * The refetchOnWindowFocus=false is an extra safety net in case of transient failures.
       * Production is unaffected: refetchOnWindowFocus defaults to true and the custom handler
       * is identical in behaviour to NextAuth's when NEXTAUTH_URL is https://.
       */}
      <SessionProvider
        refetchOnWindowFocus={process.env.NODE_ENV !== "production" ? false : true}
        refetchInterval={process.env.NODE_ENV !== "production" ? 0 : undefined}>
        <SuspendedSessionGuard />
        <TrpcProvider>
          {!isEmbed && !isBookingPage && <NotificationSoundHandler />}
          {/* @ts-expect-error FIXME remove this comment when upgrading typescript to v5 */}
          <CacheProvider>
            <WebPushProvider>
              <MobileProvider>{children}</MobileProvider>
            </WebPushProvider>
          </CacheProvider>
        </TrpcProvider>
      </SessionProvider>
    </GeoProvider>
  );
}
